import {
  DEFAULT_ICON_ID,
  DEFAULT_ZOOM,
  GEOJSON_SOURCE_ID,
  OVERLAY_SOURCE_ID,
  TARGET_ICON_HEIGHT,
  addGeocodingControl,
  cooperativeGesturesLocale,
  defaultMarkerDataUri,
  geoJsonFillLayer,
  geoJsonLineLayer,
  geoJsonPointLayer,
  imageOverlayCoordinates,
  imageRasterLayer,
  loadMapIcon,
  loadMaplibre,
  styleUrlFor,
} from './maplibre-shared';

const ENTRIES_LAYER_ID = 'project-entries';
const POPUP_OFFSET = 16;

// Public participant-facing map, with three modes chosen from the data:
//   - display pins: mapParams.topics is present. Type "topics" (homepage, topic page,
//     search) shows many entries with a click popup; type "entry" (entry detail page) shows
//     a single marker and centres on it, no popup.
//   - location input: no topics, but the surrounding form field carries [data-map-input]
//     lat/lng fields -> a draggable marker writes the chosen coordinates back to them.
// Place search is omitted for now (reintroduced via MapTiler geocoding in a later phase).
export default function maplibrePublic(originLatLng, mapParams) {
  const topics = mapParams.topics;
  const mapSettings = mapParams.mapSettings || {};
  const mapOverlay = mapParams.mapOverlay || {};
  const markerUrl = mapParams.markerUrl || defaultMarkerDataUri();

  return {
    map: null,
    maplibregl: null,
    marker: null,
    formInputs: null,

    init() {
      this.initMap();
    },

    async initMap() {
      const apiKey = this.$el.dataset.maptilerApiKey;
      if (!apiKey) {
        console.error('MapTiler API key missing on map container; aborting map init.');
        return;
      }

      this.maplibregl = await loadMaplibre();

      // The location-input forms wrap the map in [data-selector="form-field__map"] with
      // [data-map-input] lat/lng fields; their absence means this is a display map.
      this.formInputs = topics ? null : this.findFormInputs();

      const savedZoom = parseFloat(this.formInputs?.zoom?.value);
      const [centerLng, centerLat] = this.initialCenter();
      this.map = new this.maplibregl.Map({
        container: this.$refs.map,
        style: styleUrlFor(mapSettings.mapType, apiKey),
        center: [centerLng, centerLat],
        zoom: Number.isFinite(savedZoom) ? savedZoom : mapSettings.zoomLevel || DEFAULT_ZOOM,
        cooperativeGestures: true,
        locale: cooperativeGesturesLocale(),
        // On @3x iPhones the full-resolution WebGL buffer is large enough for iOS Safari
        // to degrade it behind MapLibre's back (#3686); 2x stays visually identical.
        pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      });

      this.resyncSizeWhenVisible();

      this.map.addControl(new this.maplibregl.NavigationControl({}), 'top-right');

      if (this.formInputs?.zoom) {
        this.map.on('zoomend', () => {
          this.formInputs.zoom.value = Math.round(this.map.getZoom());
        });
      }

      if (this.$el.dataset.mapSearch === 'true') {
        // On location-input forms a picked place also moves the marker and writes the
        // inputs; on display maps the control just recenters.
        const onPick = this.formInputs
          ? ({ lng, lat }) => {
              this.marker?.setLngLat([lng, lat]);
              this.writeLatLng({ lat, lng });
              this.map.setCenter([lng, lat]);
            }
          : undefined;
        addGeocodingControl(this.map, { apiKey, onPick });
      }

      this.map.on('load', () => {
        if (topics) this.addEntryLayer();
        if (this.formInputs) this.addInputMarker();
        if (mapOverlay.mapOverlayVisible) {
          this.addImageOverlay();
          this.addGeoJsonLayer();
        }
      });
    },

    // Public maps usually initialise far below the fold. iOS Safari can composite such an
    // off-screen WebGL canvas at a stale backing size, showing the map vertically squashed
    // (#3686). A resize() when the map first scrolls into view reallocates the buffer and
    // re-syncs it with the container; it is a no-op when nothing drifted.
    resyncSizeWhenVisible() {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.map.resize();
      });
      observer.observe(this.$refs.map);
    },

    findFormInputs() {
      const field = this.$refs.map.closest('[data-selector="form-field__map"]');
      if (!field) return null;
      const lat = field.querySelector('[data-map-input="lat"]');
      const lng = field.querySelector('[data-map-input="lng"]');
      if (!lat || !lng) return null;
      // Survey map fields also persist the zoom level; entry forms don't.
      return { lat, lng, zoom: field.querySelector('[data-map-input="zoom"]') };
    },

    initialCenter() {
      // An entry detail map centres on the entry; everything else on the project origin
      // (the input marker re-centres on its own coordinates once added).
      const entry = topics?.type === 'entry' ? topics.collection[0]?.entries[0] : null;
      if (entry?.position) return [entry.position.lng, entry.position.lat];
      return [originLatLng.originLng, originLatLng.originLat];
    },

    async addEntryLayer() {
      if (!topics) return;

      await loadMapIcon(this.map, DEFAULT_ICON_ID, defaultMarkerDataUri());

      const uniqueTopics = new Map();
      topics.collection.forEach((topic) => {
        if (!uniqueTopics.has(topic.id)) uniqueTopics.set(topic.id, topic);
      });

      // Pin icon fallback chain: topic icon -> project custom marker -> default pin SVG.
      // The raw param is used (not the defaulted markerUrl const): the S3 default PNG
      // serves no CORS headers, so it can't be sampled into a WebGL texture anyway —
      // topics with no URL go straight to the SVG via the layer's coalesce.
      await Promise.all(
        Array.from(uniqueTopics.values())
          .map((topic) => ({ topic, url: topic.iconUrl || mapParams.markerUrl }))
          .filter(({ url }) => url)
          .map(({ topic, url }) => loadMapIcon(this.map, `topic-icon-${topic.id}`, url))
      );

      const features = [];
      topics.collection.forEach((topic) => {
        topic.entries.forEach((entry) => {
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [entry.position.lng, entry.position.lat],
            },
            properties: {
              topicId: topic.id,
              entryId: entry.id,
              title: entry.title.replace(/<[^>]*>?/gm, ''),
              iconImageId: `topic-icon-${topic.id}`,
            },
          });
        });
      });

      this.map.addSource(ENTRIES_LAYER_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features },
      });

      this.map.addLayer({
        id: ENTRIES_LAYER_ID,
        type: 'symbol',
        source: ENTRIES_LAYER_ID,
        layout: {
          'icon-image': ['coalesce', ['image', ['get', 'iconImageId']], ['image', DEFAULT_ICON_ID]],
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
          'icon-size': 1,
        },
      });

      // Only the multi-entry "topics" maps open a popup with a link to the entry; a single
      // "entry" detail map just shows the marker.
      if (topics.type === 'topics') this.attachEntryPopups();
    },

    attachEntryPopups() {
      this.map.on('click', ENTRIES_LAYER_ID, (event) => {
        const feature = event.features && event.features[0];
        if (!feature) return;

        const { topicId, entryId, title } = feature.properties;
        const popupHtml =
          `<div class="entry-excerpt">` +
          `<h3>${title}</h3>` +
          `<a href="topics/${topicId}/entries/${entryId}" data-turbo-frame="_top" class="link">${
            mapSettings.detailsLink || 'Details'
          }</a>` +
          '</div>';

        new this.maplibregl.Popup({ offset: POPUP_OFFSET })
          .setLngLat(feature.geometry.coordinates)
          .setHTML(popupHtml)
          .addTo(this.map);
      });

      this.map.on('mouseenter', ENTRIES_LAYER_ID, () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', ENTRIES_LAYER_ID, () => {
        this.map.getCanvas().style.cursor = '';
      });
    },

    // Draggable location-input marker: click the map or drag the marker to choose a point,
    // writing the coordinates back to the form's [data-map-input] fields.
    addInputMarker() {
      const { lat: latInput, lng: lngInput } = this.formInputs;
      const lat = parseFloat(latInput.value || originLatLng.originLat);
      const lng = parseFloat(lngInput.value || originLatLng.originLng);

      const icon = document.createElement('img');
      icon.src = markerUrl;
      icon.style.height = `${TARGET_ICON_HEIGHT}px`;
      icon.style.cursor = 'grab';

      this.marker = new this.maplibregl.Marker({ element: icon, anchor: 'bottom', draggable: true })
        .setLngLat([lng, lat])
        .addTo(this.map);
      this.map.setCenter([lng, lat]);

      this.map.on('click', (event) => {
        this.marker.setLngLat(event.lngLat);
        this.writeLatLng(event.lngLat);
      });
      this.marker.on('dragend', () => this.writeLatLng(this.marker.getLngLat()));
    },

    writeLatLng({ lat, lng }) {
      this.formInputs.lat.value = lat;
      this.formInputs.lng.value = lng;
    },

    addImageOverlay() {
      const url = mapOverlay.mapOverlayImageUrl;
      const north = parseFloat(mapOverlay.overlayBoundNorth);
      const south = parseFloat(mapOverlay.overlayBoundSouth);
      const east = parseFloat(mapOverlay.overlayBoundEast);
      const west = parseFloat(mapOverlay.overlayBoundWest);
      if (!url || [north, south, east, west].some(Number.isNaN)) return;

      this.map.addSource(OVERLAY_SOURCE_ID, {
        type: 'image',
        url,
        coordinates: imageOverlayCoordinates({ north, south, east, west }),
      });
      this.map.addLayer(imageRasterLayer());
    },

    addGeoJsonLayer() {
      if (!mapParams.geoJsonUrl) return;

      this.map.addSource(GEOJSON_SOURCE_ID, { type: 'geojson', data: mapParams.geoJsonUrl });
      this.map.addLayer(geoJsonFillLayer());
      this.map.addLayer(geoJsonLineLayer());
      this.map.addLayer(geoJsonPointLayer());
    },
  };
}
